/**
 * Cache plumbing for the community sentiment store. One cache entry per target rather than one
 * per request, so a batched marquee read and a Detail read for the same title share it. Batching
 * happens at the network layer instead (see `sentimentBatchLoader`).
 */

import { QueryClient } from '@tanstack/react-query';

import { SENTIMENT_CACHE_KEY, SENTIMENT_FEEDBACK_TYPE } from './constants';

/** What the store holds for a single target. `null` means "known to be unset", not "unknown". */
export type SentimentEntry = {
    feedbackId: string | null;
    /** Emoji token, or `null` when unset. */
    value: string | null;
    updatedAt: string | null;
};

export const EMPTY_SENTIMENT: SentimentEntry = {
    feedbackId: null,
    value: null,
    updatedAt: null,
};

export type SentimentTarget = {
    targetType: string;
    targetId: string;
};

/** Snapshot of one entry, used to roll an optimistic write back. Mirrors `MyListCarouselSnapshot`. */
export type SentimentSnapshot = {
    queryKey: readonly unknown[];
    data: SentimentEntry | undefined;
};

/** Key for one target's rating. `feedbackType` is part of it so reviews cannot collide with reactions. */
export function sentimentQueryKey({
    targetType,
    targetId,
    feedbackType = SENTIMENT_FEEDBACK_TYPE.REACTION,
}: SentimentTarget & { feedbackType?: string }): readonly unknown[] {
    return [SENTIMENT_CACHE_KEY, { targetType, targetId, feedbackType }];
}

/** True for any query in this store, whatever its target. Used for broad invalidation. */
export function isSentimentQuery(query: { queryKey: unknown }): boolean {
    return Array.isArray(query.queryKey) && query.queryKey[0] === SENTIMENT_CACHE_KEY;
}

let sentimentGeneration = 0;

/** Called only by the auth-change reset; a write checks it via {@link getSentimentGeneration}. */
export function bumpSentimentGeneration(): number {
    return (sentimentGeneration += 1);
}

export function getSentimentGeneration(): number {
    return sentimentGeneration;
}

export function optimisticallyApplySentiment(
    queryClient: QueryClient,
    target: SentimentTarget & { feedbackType?: string },
    value: string | null,
): SentimentSnapshot {
    const queryKey = sentimentQueryKey(target);
    const previous = queryClient.getQueryData<SentimentEntry>(queryKey);

    queryClient.setQueryData<SentimentEntry>(queryKey, {
        // Keep the existing id through a change; a fresh rating has none until the server issues one.
        feedbackId: value === null ? null : (previous?.feedbackId ?? null),
        value,
        updatedAt: previous?.updatedAt ?? null,
    });

    return { queryKey, data: previous };
}

/** Restores a snapshot taken by {@link optimisticallyApplySentiment}. */
export function restoreSentimentSnapshot(
    queryClient: QueryClient,
    snapshot: SentimentSnapshot | undefined,
): void {
    if (!snapshot) return;

    if (snapshot.data === undefined) {
        // No entry before the write; leaving the optimistic value would strand a rating the
        // server never accepted.
        queryClient.removeQueries({ queryKey: snapshot.queryKey, exact: true });
        return;
    }

    queryClient.setQueryData(snapshot.queryKey, snapshot.data);
}

/** Commits the row the server returned, replacing whatever the optimistic write guessed. */
export function commitSentimentEntry(
    queryClient: QueryClient,
    target: SentimentTarget & { feedbackType?: string },
    entry: SentimentEntry,
): void {
    queryClient.setQueryData<SentimentEntry>(sentimentQueryKey(target), entry);
}

export async function invalidateSentimentQueries(queryClient: QueryClient): Promise<void> {
    await queryClient.invalidateQueries({ predicate: isSentimentQuery });
}
